// @gs/feedback/client — TicketThread : fil + reactions + triage staff + notes
// internes staff + historique. DS V2 (CTA slate, chips subtils, bi-mode).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Archive, ArchiveRestore, Bell, BellOff, Download, Paperclip, Pencil, SmilePlus, Trash2, X } from 'lucide-react';
import { useTranslation } from '@gs/i18n';
import { GsBreadcrumb, useToast } from '@gs/ui';
import {
  ARCHIVED_STATUS,
  BODY_MAX,
  FEEDBACK_PRIORITIES,
  FEEDBACK_STATUSES,
  MAX_ATTACHMENTS,
  REACTION_EMOJIS,
  nextLifecycleStatus,
  quoteSnippet,
  type MessageDto,
} from '../schemas';
import {
  uploadAttachment, useAddNote, useDeleteMessage, useEditMessage, useFollow, useMarkRead,
  useSendMessage, useTicketActivity, useTicketAttachments, useTicketDetail,
  useTicketMessages, useTicketNotes, useToggleReaction, useTriage,
  type AttachmentDto,
} from './hooks';
import {
  BTN_DANGER_OUTLINE, BTN_PRIMARY, BTN_SECONDARY, CHIP_BASE, COMPANY_CHIP, INPUT_BASE,
  ORIGIN_APP_CHIP, PRIORITY_CHIP, STAFF_BADGE_CHIP, STATUS_CHIP, TYPE_CHIP, formatDateTime,
} from './chips';
import { originAppLabel } from './originApp';
import { attachmentKind } from './attachmentKind';
import { firstUnseenMessageId } from './firstUnseen';
import { downloadAttachment, openAttachmentInTab, useAttachmentObjectUrl } from './attachmentContent';
import { addPickedFiles, filesFromDataTransfer, imageFilesFromClipboard, type FileIntakeErrorKey } from './composerFiles';
import { MessageQuote } from './MessageQuote';

export interface TicketThreadProps {
  ticketId: string;
  onBack: () => void;
}

/** Sentinel navigation fil d'Ariane — retour liste (le module ne connait pas les routes hote). */
const FEEDBACK_LIST_NAV = '__feedback_list__';

function FeedbackBreadcrumb({
  ticketNumber,
  onBack,
}: {
  ticketNumber?: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const items = ticketNumber
    ? [{ label: t('feedback:list.title'), to: FEEDBACK_LIST_NAV }, { label: ticketNumber }]
    : [{ label: t('feedback:list.title'), to: FEEDBACK_LIST_NAV }];
  return (
    <GsBreadcrumb
      ariaLabel={t('feedback:thread.breadcrumbAria')}
      items={items}
      onNavigate={(to) => { if (to === FEEDBACK_LIST_NAV) onBack(); }}
    />
  );
}

export function TicketThread({ ticketId, onBack }: TicketThreadProps) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<'conversation' | 'history'>('conversation');
  const detail = useTicketDetail(ticketId);
  const messagesQ = useTicketMessages(ticketId);
  const { mutate: markReadMutate } = useMarkRead();

  // Snapshot du marqueur « lu jusqu'a » A L'OUVERTURE (CHORE-FBK-INBOX-V2,
  // separateur « Nouveaux messages ») : l'ouverture marque lu (mutation
  // ci-dessous) et le refetchInterval rafraichit ensuite lastSeenAt — le
  // separateur doit rester ancre sur l'etat d'AVANT l'ouverture, et disparaitre
  // seulement a la PROCHAINE ouverture. Pattern « adjust state during render »
  // (cf. FeedbackPage — setState synchrone en useEffect rejete par le React
  // Compiler). show=false (ticket deja lu) => aucun separateur.
  const [snapshot, setSnapshot] = useState<{ ticketId: string; show: boolean; lastSeenAt: string | null } | null>(null);
  if (detail.data && snapshot?.ticketId !== ticketId) {
    setSnapshot({
      ticketId,
      show: detail.data.ticket.unread,
      lastSeenAt: detail.data.ticket.lastSeenAt,
    });
  }

  // Marquer lu a l'ouverture + quand de nouveaux messages arrivent.
  const msgCount = messagesQ.data?.messages.length ?? 0;
  useEffect(() => {
    markReadMutate(ticketId);
  }, [ticketId, msgCount, markReadMutate]);

  if (detail.isError || messagesQ.isError) {
    return (
      <div className="space-y-3">
        <FeedbackBreadcrumb ticketNumber="…" onBack={onBack} />
        <div className="rounded-xl bg-white p-6 text-center text-meta text-slate-500 shadow-sm dark:bg-slate-900 dark:text-slate-400">
          <p>{t('feedback:thread.loadError')}</p>
        </div>
      </div>
    );
  }
  if (!detail.data) {
    return <div className="h-40 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" aria-busy="true" />;
  }

  const { ticket, staff, currentUserId } = detail.data;
  const threadMessages = messagesQ.data?.messages ?? [];
  const messageCount =
    threadMessages.filter((m) => m.kind !== 'STATUS_CHANGE').length + 1;

  return (
    <div className="space-y-3">
      <FeedbackBreadcrumb ticketNumber={ticket.ticketNumber} onBack={onBack} />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-start gap-3 px-3.5 py-3">
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-field font-bold ${TYPE_CHIP[ticket.type]}`}
            aria-hidden
          >
            {ticket.type.slice(0, 1)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="text-head font-bold text-slate-900 dark:text-slate-50">{ticket.title}</h2>
              <span className={`${CHIP_BASE} ${TYPE_CHIP[ticket.type]}`}>{t(`feedback:type.${ticket.type}`)}</span>
              <span className={`${CHIP_BASE} ${ORIGIN_APP_CHIP}`}
                title={t('feedback:originAppChipTitle', { app: originAppLabel(t, ticket.originBcKey) })}>
                {originAppLabel(t, ticket.originBcKey)}
              </span>
              <span className={`${CHIP_BASE} ${STATUS_CHIP[ticket.status]}`}>{t(`feedback:status.${ticket.status}`)}</span>
              <span className={`${CHIP_BASE} ${PRIORITY_CHIP[ticket.priority]}`}>{t(`feedback:priority.${ticket.priority}`)}</span>
              {(ticket.companyName ?? ticket.companyCode) && (
                <span className={`${CHIP_BASE} ${COMPANY_CHIP}`}
                  title={t('feedback:companyOrigin', { name: ticket.companyName ?? ticket.companyCode })}>
                  {ticket.companyName ?? ticket.companyCode}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-meta text-slate-400">
              {t('feedback:thread.createdBy', { name: ticket.createdByName })} · {formatDateTime(ticket.createdAt, i18n.language)}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            {staff && <ArchiveButton ticketId={ticketId} status={ticket.status} />}
            <FollowButton ticketId={ticketId} following={ticket.isFollowing} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-px border-t border-slate-200 bg-slate-200 dark:border-slate-700 dark:bg-slate-700">
          {[
            { label: t('feedback:thread.metricMessages'), value: String(messageCount) },
            { label: t('feedback:thread.metricLastActivity'), value: formatDateTime(ticket.lastActivityAt, i18n.language) },
            { label: t('feedback:thread.metricOpenedAt'), value: formatDateTime(ticket.createdAt, i18n.language) },
          ].map((m) => (
            <div key={m.label} className="bg-white px-3.5 py-2.5 dark:bg-slate-800">
              <div className="text-chip font-semibold uppercase tracking-wide text-slate-400">{m.label}</div>
              <div className="mt-0.5 truncate text-field font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                {m.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-1">
        {(['conversation', 'history'] as const).map((tb) => (
          <button key={tb} type="button" onClick={() => setTab(tb)} aria-pressed={tab === tb}
            className={[
              'touch-manipulation rounded-lg px-2.5 py-1 text-meta font-semibold transition-colors disabled:opacity-45',
              tab === tb
                ? BTN_PRIMARY
                : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
            ].join(' ')}>
            {t(`feedback:thread.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'conversation' ? (
        <>
          <SeedAndMessages
            ticketId={ticketId}
            description={ticket.description}
            createdByName={ticket.createdByName}
            createdAt={ticket.createdAt}
            messages={messagesQ.data?.messages ?? []}
            currentUserId={currentUserId}
            staff={staff}
            newBoundary={snapshot?.show && snapshot.ticketId === ticketId ? { lastSeenAt: snapshot.lastSeenAt } : null}
          />
          {staff && (
            <TriagePanel
              key={`${ticket.status}-${ticket.priority}-${ticket.adminNotes ?? ''}`}
              ticketId={ticketId}
              status={ticket.status}
              priority={ticket.priority}
              adminNotes={ticket.adminNotes}
            />
          )}
          {staff && <NotesPanel ticketId={ticketId} />}
        </>
      ) : (
        <HistoryPanel ticketId={ticketId} active={tab === 'history'} />
      )}
    </div>
  );
}

function FollowButton({ ticketId, following }: { ticketId: string; following: boolean }) {
  const { t } = useTranslation();
  const follow = useFollow(ticketId);
  return (
    <button type="button" onClick={() => follow.mutate(!following)} disabled={follow.isPending}
      aria-pressed={following}
      className={`touch-manipulation ${BTN_SECONDARY} inline-flex shrink-0 items-center gap-1.5 disabled:opacity-45`}>
      {following ? <BellOff size={13} /> : <Bell size={13} />}
      {following ? t('feedback:thread.following') : t('feedback:thread.follow')}
    </button>
  );
}

/**
 * Archiver / Rouvrir un fil (CHORE-FBK-LIFECYCLE, staff seulement). Reutilise le
 * MEME PATCH triage que le TriagePanel (useTriage) : archiver = statut terminal
 * ARCHIVED ; rouvrir = REOPEN_STATUS (« En cours »). Reversible et trace cote
 * serveur (StatusHistory + Activity + message systeme). Aucun endpoint, aucun
 * flag, aucune migration — le statut ARCHIVED existe deja (mig 116).
 */
function ArchiveButton({ ticketId, status }: { ticketId: string; status: string }) {
  const { t } = useTranslation();
  const triage = useTriage(ticketId);
  const archived = status === ARCHIVED_STATUS;
  return (
    <button type="button" disabled={triage.isPending}
      onClick={() => triage.mutate({ status: nextLifecycleStatus(status) })}
      aria-label={archived ? t('feedback:thread.reopen') : t('feedback:thread.archive')}
      className={`touch-manipulation inline-flex shrink-0 items-center gap-1.5 disabled:opacity-45 ${
        archived ? BTN_SECONDARY : BTN_DANGER_OUTLINE
      }`}>
      {archived ? <ArchiveRestore size={13} /> : <Archive size={13} />}
      {archived ? t('feedback:thread.reopen') : t('feedback:thread.archive')}
    </button>
  );
}

interface SeedAndMessagesProps {
  ticketId: string;
  description: string;
  createdByName: string;
  createdAt: string;
  messages: MessageDto[];
  currentUserId: string;
  staff: boolean;
  /** Snapshot « lu jusqu'a » a l'ouverture (CHORE-FBK-INBOX-V2) — null = ticket
   *  deja lu a l'ouverture, aucun separateur. lastSeenAt null = jamais lu. */
  newBoundary?: { lastSeenAt: string | null } | null;
}

// `staff` reste dans le contrat (reserve — note interne par message a venir)
// mais n'est pas consomme ici : prefixe _ (convention unused-vars du repo).
function SeedAndMessages({ ticketId, description, createdByName, createdAt, messages, currentUserId, staff: _staff, newBoundary }: SeedAndMessagesProps) {
  const { t, i18n } = useTranslation();
  const [replyTo, setReplyTo] = useState<MessageDto | null>(null);
  const [draft, setDraft] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Glisser-deposer (CHORE-FBK-COMPOSER-V2) : surbrillance de la zone composer.
  const [dragActive, setDragActive] = useState(false);
  // Scroll au message cite (clic sur le bloc citation) + flash defensif.
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const [flashId, setFlashId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const send = useSendMessage(ticketId);
  const edit = useEditMessage(ticketId);
  const remove = useDeleteMessage(ticketId);
  const react = useToggleReaction(ticketId);
  const attachments = useTicketAttachments(ticketId);
  const [messageRenderNow] = useState(() => Date.now());

  /** « Repondre » = mode citation : chip dans le composer + focus auto. */
  const startReply = (m: MessageDto) => {
    setReplyTo(m);
    textareaRef.current?.focus?.();
  };

  /** Clic sur une citation : scroll au message cite + flash defensif (jsdom-safe). */
  const jumpToMessage = (id: string) => {
    const el = messageRefs.current.get(id);
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    setFlashId(id);
    window.setTimeout(() => setFlashId((p) => (p === id ? null : p)), 1600);
  };

  // Arbre profondeur 1 : racines + reponses rattachees (orphelines re-promues).
  const roots = useMemo(() => {
    const rootList = messages.filter((m) => !m.parentMessageId);
    const rootIds = new Set(rootList.map((m) => m.id));
    const byParent = new Map<string, MessageDto[]>();
    for (const m of messages) {
      if (!m.parentMessageId) continue;
      const pid = rootIds.has(m.parentMessageId) ? m.parentMessageId : null;
      if (pid === null) { rootList.push(m); rootIds.add(m.id); continue; }
      byParent.set(pid, [...(byParent.get(pid) ?? []), m]);
    }
    rootList.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return { rootList, byParent };
  }, [messages]);

  // Separateur « Nouveaux messages » (CHORE-FBK-INBOX-V2, pattern Slack) : place
  // avant le premier message posterieur au snapshot (helper PUR firstUnseen) +
  // scroll automatique dessus (defensif : scrollIntoView absent en jsdom).
  const firstNewId = useMemo(
    () => (newBoundary ? firstUnseenMessageId(messages, newBoundary.lastSeenAt, currentUserId) : null),
    [newBoundary, messages, currentUserId],
  );
  const separatorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (firstNewId) separatorRef.current?.scrollIntoView?.({ block: 'center' });
  }, [firstNewId]);

  const ticketAttachments = (attachments.data ?? []).filter((a) => a.parent === 'ticket');
  // PJ par MESSAGE (parite POC MessageComposer) : le serveur les supporte depuis
  // la mig 116 (fbk.Attachments.message_id) — affichees sous le message concerne.
  const messageAttachments = useMemo(() => {
    const map = new Map<string, AttachmentDto[]>();
    for (const a of attachments.data ?? []) {
      if (a.parent !== 'message') continue;
      map.set(a.parentId, [...(map.get(a.parentId) ?? []), a]);
    }
    return map;
  }, [attachments.data]);

  // Admission des PJ — UN seul chemin (addPickedFiles, helpers PURS) pour le
  // trombone, le glisser-deposer ET le collage : memes contraintes que la
  // creation (max 3, 10 Mo/fichier, image+pdf), AUCUNE regle dupliquee.
  const intakeErrorLabel = (key: FileIntakeErrorKey): string | null => {
    if (key === 'type') return t('feedback:modal.attachmentTypeError');
    if (key === 'size') return t('feedback:modal.attachmentSizeError');
    if (key === 'count') return t('feedback:modal.attachmentCountError', { max: MAX_ATTACHMENTS });
    return null;
  };
  const intakeFiles = (incoming: Iterable<File>) => {
    const result = addPickedFiles(files, incoming);
    setFiles(result.files);
    setFileError(intakeErrorLabel(result.errorKey));
  };
  const onFilesPicked = (picked: FileList | null) => {
    if (!picked) return;
    intakeFiles(Array.from(picked));
  };

  const submit = async () => {
    const body = draft.trim();
    if (!body || send.isPending) return;
    const created = await send.mutateAsync({
      body,
      // Quote-reply (CHORE-FBK-COMPOSER-V2) : reference du message CITE — le
      // composer n'emet plus parentMessageId (threading legacy, lecture seule).
      replyToId: replyTo?.id,
      clientMsgId: crypto.randomUUID(),
    });
    // PJ apres creation du message (parent=message) — echec PJ non bloquant,
    // pattern FeedbackModal. Refetch pour afficher sous le message.
    for (const f of files) {
      await uploadAttachment(ticketId, f, 'message', created.id).catch(() => { /* fail-soft PJ */ });
    }
    if (files.length > 0) void attachments.refetch();
    setFiles([]);
    setFileError(null);
    setDraft('');
    setReplyTo(null);
  };

  // Separateur « Nouveaux messages » (liseré + libellé, tokens DS, pattern
  // Slack) — rendu avant le premier message non vu (y compris une legende
  // STATUS_CHANGE : elle compte dans unread_count) ; disparait a la prochaine
  // ouverture (le snapshot suit le marqueur avance par cette lecture).
  const newSeparator = (
    <div ref={separatorRef} role="separator" aria-label={t('feedback:thread.newMessages')}
      className="my-2 flex items-center gap-2">
      <span className="h-px flex-1 bg-brand-200 dark:bg-brand-900" aria-hidden />
      <span className="text-chip font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
        {t('feedback:thread.newMessages')}
      </span>
      <span className="h-px flex-1 bg-brand-200 dark:bg-brand-900" aria-hidden />
    </div>
  );

  const renderMessage = (m: MessageDto, isReply: boolean) => {
    const own = m.authorUserId === currentUserId;
    const within15 = messageRenderNow - new Date(m.createdAt).getTime() < 15 * 60_000; // UX seulement — l'autorite est SERVEUR
    if (m.kind === 'STATUS_CHANGE') {
      // Légende système inline = chrome (11px), pas du contenu de lecture.
      return (
        <div key={m.id}>
          {m.id === firstNewId && newSeparator}
          <div className="my-2 text-center text-meta text-slate-400">
            {m.authorName} · {m.body} · {formatDateTime(m.createdAt, i18n.language)}
          </div>
        </div>
      );
    }
    return (
      <div key={m.id} className={isReply ? 'ml-8' : ''}
        ref={(el) => { if (el) messageRefs.current.set(m.id, el); else messageRefs.current.delete(m.id); }}>
        {m.id === firstNewId && newSeparator}
        {/* Bulle de LECTURE : px-4 py-3, méta 12px, corps 14px leading-relaxed (UI_STANDARDS §Échelle de lecture).
            Flash défensif post-scroll (clic citation) : ring temporaire aux tokens. */}
        <div className={[
          `rounded-xl px-4 py-3 ${own ? 'bg-slate-100 dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/50'}`,
          m.id === flashId ? 'ring-2 ring-brand-300 dark:ring-brand-800 transition-shadow' : '',
        ].join(' ')}>
          <div className="flex items-center gap-1.5 text-meta text-slate-400">
            <span className="font-semibold text-slate-600 dark:text-slate-300">{m.authorName}</span>
            {m.authorRole === 'ADMIN' && (
              <span className={`${CHIP_BASE} ${STAFF_BADGE_CHIP}`}>ADMIN</span>
            )}
            <span>{formatDateTime(m.createdAt, i18n.language)}</span>
            {m.editedAt && !m.deletedAt && <span className="italic">({t('feedback:thread.edited')})</span>}
          </div>
          {/* Citation quote-reply (CHORE-FBK-COMPOSER-V2) : bloc cliquable au-dessus
              du corps — clic = scroll au message cite + flash. */}
          {m.replyTo && !m.deletedAt && (
            <div className="mt-1.5">
              <MessageQuote
                authorName={m.replyTo.authorName}
                snippet={m.replyTo.snippet}
                deletedLabel={t('feedback:thread.deleted')}
                jumpLabel={t('feedback:thread.quoteJump')}
                onJump={() => jumpToMessage(m.replyTo!.id)}
              />
            </div>
          )}
          {editing?.id === m.id ? (
            <div className="mt-1.5 space-y-1.5">
              <textarea className={`${INPUT_BASE} min-h-15`} value={editing.body} maxLength={BODY_MAX}
                onChange={(e) => setEditing({ id: m.id, body: e.target.value })} />
              <div className="flex gap-1.5">
                <button type="button" className={`touch-manipulation ${BTN_PRIMARY} disabled:opacity-45`}
                  onClick={() => { void edit.mutateAsync({ messageId: m.id, body: editing.body.trim() }).then(() => setEditing(null)).catch(() => alert(t('feedback:thread.windowClosed'))); }}>
                  {t('feedback:thread.save')}
                </button>
                <button type="button" className={`touch-manipulation ${BTN_SECONDARY} disabled:opacity-45`} onClick={() => setEditing(null)}>
                  {t('feedback:thread.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <p className={`mt-1.5 whitespace-pre-wrap text-body leading-relaxed ${m.deletedAt ? 'italic text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
              {m.deletedAt ? t('feedback:thread.deleted') : m.body}
            </p>
          )}
          <MessageAttachments items={messageAttachments.get(m.id) ?? []} />
          {!m.deletedAt && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1">
              {m.reactions.map((r) => (
                <button key={r.emoji} type="button" aria-pressed={r.mine}
                  onClick={() => react.mutate({ messageId: m.id, emoji: r.emoji, active: !r.mine })}
                  className={[
                    'touch-manipulation rounded-full px-1.5 py-0.5 text-chip transition-colors',
                    r.mine
                      ? 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200 dark:bg-brand-950/40 dark:text-brand-300 dark:ring-brand-900'
                      : 'bg-white text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:ring-slate-700',
                  ].join(' ')}>
                  {r.emoji} {r.count}
                </button>
              ))}
              <ReactionPicker onPick={(emoji) => react.mutate({ messageId: m.id, emoji, active: true })} />
              {/* Quote-reply : repondre a N'IMPORTE quel message (plat — la
                  citation porte la reference, plus d'indentation a poster). */}
              <button type="button" onClick={() => startReply(m)}
                className="touch-manipulation ml-1 text-chip font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                {t('feedback:thread.reply')}
              </button>
              {own && within15 && (
                <>
                  <button type="button" onClick={() => setEditing({ id: m.id, body: m.body })}
                    aria-label={t('feedback:thread.edit')}
                    className="touch-manipulation rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                    <Pencil size={11} />
                  </button>
                  <button type="button"
                    onClick={() => { if (window.confirm(t('feedback:thread.confirmDelete'))) remove.mutateAsync(m.id).catch(() => alert(t('feedback:thread.windowClosed'))); }}
                    aria-label={t('feedback:thread.delete')}
                    className="touch-manipulation rounded p-0.5 text-slate-400 hover:text-red-500">
                    <Trash2 size={11} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        {(roots.byParent.get(m.id) ?? []).map((child) => (
          <div key={child.id} className="mt-2">{renderMessage(child, true)}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3 rounded-xl bg-white px-4 py-4 shadow-sm dark:bg-slate-900">
      {/* Description du ticket = message racine (parite POC SeedMessage).
          Echelle de lecture : meta 12px, corps 14px leading-relaxed, bloc px-4 py-3. */}
      <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3 dark:border-slate-800 dark:bg-slate-800/40">
        <div className="text-meta text-slate-400">
          <span className="font-semibold text-slate-600 dark:text-slate-300">{createdByName}</span>{' '}
          · {formatDateTime(createdAt, i18n.language)}
        </div>
        <p className="mt-1.5 whitespace-pre-wrap text-body leading-relaxed text-slate-700 dark:text-slate-200">{description}</p>
        <MessageAttachments items={ticketAttachments} />
      </div>

      {roots.rootList.length === 0 ? (
        <p className="py-3 text-center text-meta text-slate-400">{t('feedback:thread.emptyThread')}</p>
      ) : (
        <div className="space-y-3">{roots.rootList.map((m) => renderMessage(m, false))}</div>
      )}

      {/* Zone composer = dropzone (CHORE-FBK-COMPOSER-V2) : glisser-deposer
          multi-fichiers + surbrillance dragover aux tokens ; le collage (Ctrl+V
          de capture d'ecran) vit sur le textarea. Meme chemin d'admission que
          le trombone (intakeFiles). */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          intakeFiles(filesFromDataTransfer(e.dataTransfer));
        }}
        className={[
          'border-t border-slate-100 pt-3 dark:border-slate-800',
          dragActive ? 'rounded-lg ring-2 ring-brand-300 bg-brand-50/30 dark:ring-brand-800 dark:bg-brand-950/20' : '',
        ].join(' ')}
      >
        {replyTo && (
          /* Chip de citation (mode reponse) : auteur + extrait, X pour annuler
             (Echap dans le textarea annule aussi). Meme grammaire que le bloc
             citation rendu (liseré gauche neutre). */
          <div className="mb-2 flex items-center justify-between gap-2 rounded-md border-l-2 border-slate-300 bg-slate-50 px-2.5 py-1.5 dark:border-slate-600 dark:bg-slate-800">
            <span className="min-w-0 text-meta text-slate-500 dark:text-slate-400">
              <span className="font-semibold">{t('feedback:thread.replyingTo', { name: replyTo.authorName })}</span>
              <span className="ml-1.5 truncate text-slate-400 dark:text-slate-500">{quoteSnippet(replyTo.body)}</span>
            </span>
            <button type="button" onClick={() => setReplyTo(null)}
              aria-label={t('feedback:thread.cancelReply')}
              title={t('feedback:thread.cancelReply')}
              className="touch-manipulation shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
              <X size={12} />
            </button>
          </div>
        )}
        {files.length > 0 && (
          <ul className="mb-2 space-y-1">
            {files.map((f, idx) => (
              <li key={`${f.name}-${idx}`}
                className="flex items-center justify-between rounded-md bg-slate-50 px-2.5 py-1.5 text-meta text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <span className="truncate">{f.name} · {Math.ceil(f.size / 1024)} Ko</span>
                <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                  aria-label={`${t('feedback:thread.cancel')} ${f.name}`}
                  className="touch-manipulation ml-2 shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {fileError && (
          <p className="mb-2 rounded-md bg-red-50 px-2.5 py-1.5 text-meta text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
            {fileError}
          </p>
        )}
        <div className="flex items-end gap-2">
          {/* Joindre — parite POC MessageComposer ; memes contraintes que la creation. */}
          <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf"
            className="hidden" onChange={(e) => { onFilesPicked(e.target.files); e.target.value = ''; }} />
          <button type="button" onClick={() => fileInputRef.current?.click()}
            disabled={files.length >= MAX_ATTACHMENTS || send.isPending}
            aria-label={t('feedback:modal.addAttachment')}
            className="touch-manipulation rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-45 dark:hover:bg-slate-800 dark:hover:text-slate-300">
            <Paperclip size={15} />
          </button>
          <textarea ref={textareaRef} className={`${INPUT_BASE} min-h-11 flex-1 resize-y`} value={draft} maxLength={BODY_MAX}
            placeholder={t('feedback:thread.messagePlaceholder')}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); }
              if (e.key === 'Escape' && replyTo) { e.preventDefault(); setReplyTo(null); }
            }}
            onPaste={(e) => {
              // Ctrl+V de capture d'ecran (le geste roi du support) : on
              // n'intercepte QUE les images — un collage de texte suit son
              // chemin natif vers le textarea.
              const images = imageFilesFromClipboard(e.clipboardData);
              if (images.length > 0) { e.preventDefault(); intakeFiles(images); }
            }} />
          <button type="button" className={`touch-manipulation ${BTN_PRIMARY} disabled:opacity-45`} disabled={send.isPending || !draft.trim()}
            onClick={() => void submit()}>
            {send.isPending ? t('feedback:thread.sending') : t('feedback:thread.send')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** PJ d'un message / du seed (CHORE-FBK-PJ-PREVIEW, norme GitHub/Linear/Slack) :
 *  image = vignette lazy + lightbox au clic ; PDF = nouvel onglet (visualisateur
 *  NATIF du navigateur — Content-Disposition inline servi par l'APP) ; autre
 *  type = telechargement. L'action « telecharger » reste offerte pour TOUS les
 *  types. CHORE-FBK-PJ-PROXY : `url`/`downloadUrl` = endpoints de l'app (Bearer
 *  requis) — tout passe par les object URLs de attachmentContent.ts (cache de
 *  page, plus d'expiration). AUCUN href nu vers /content ou /download (un clic
 *  en URL nue n'emporte pas le Bearer -> JSON 401 dans l'onglet, dogfood Marc
 *  2026-06-12 — garde-test noNakedAttachmentHref). Echec de chargement = toast
 *  i18n (CHORE-FBK-PJ-PROXY-FIX ; exige le ToastProvider de l'app hote — ug
 *  l'a au root). Dispatch = helper pur attachmentKind. */
function MessageAttachments({ items }: { items: AttachmentDto[] }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [lightbox, setLightbox] = useState<AttachmentDto | null>(null);
  if (items.length === 0) return null;
  const toastIfFailed = (ok: boolean) => {
    if (!ok) toast(t('feedback:thread.attachmentLoadError'), 'error');
  };
  return (
    <>
      <ul className="mt-2 flex flex-wrap items-end gap-2">
        {items.map((a) => {
          const kind = attachmentKind(a.contentType);
          return (
            <li key={a.id} className="flex items-center gap-1">
              {kind === 'image' ? (
                <button type="button" onClick={() => setLightbox(a)}
                  aria-label={t('feedback:thread.openPreview', { name: a.filename })}
                  className="touch-manipulation overflow-hidden rounded-lg border border-slate-200 transition-opacity hover:opacity-90 dark:border-slate-700">
                  <AttachmentImage path={a.url} alt={a.filename}
                    className="max-h-32 max-w-52 object-cover" />
                </button>
              ) : (
                <button type="button"
                  onClick={() => {
                    if (kind === 'pdf') void openAttachmentInTab(a.url).then(toastIfFailed);
                    else void downloadAttachment(a.downloadUrl, a.filename).then(toastIfFailed);
                  }}
                  className="touch-manipulation inline-flex items-center gap-1 text-meta font-medium text-sky-700 underline-offset-2 hover:underline dark:text-sky-400">
                  <Paperclip size={11} aria-hidden /> {a.filename}
                </button>
              )}
              <button type="button"
                onClick={() => void downloadAttachment(a.downloadUrl, a.filename).then(toastIfFailed)}
                aria-label={t('feedback:thread.download', { name: a.filename })}
                title={t('feedback:thread.download', { name: a.filename })}
                className="touch-manipulation rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                <Download size={11} aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
      {lightbox && <AttachmentLightbox item={lightbox} onClose={() => setLightbox(null)} />}
    </>
  );
}

/** <img> dont la source est resolue par fetch authentifie (object URL, cache de
 *  page). Pendant le chargement : placeholder pulse aux dimensions de la vignette
 *  (aucun texte — l'aria du bouton parent porte le nom). */
function AttachmentImage({ path, alt, className }: { path: string; alt: string; className: string }) {
  const url = useAttachmentObjectUrl(path);
  if (!url) {
    return <div aria-hidden className={`${className} h-24 w-32 animate-pulse bg-slate-100 dark:bg-slate-700`} />;
  }
  return <img src={url} alt={alt} loading="lazy" className={className} />;
}

/** Lightbox plein ecran (image seulement) — fond dim, fermeture Esc/clic fond/X,
 *  alt = filename. Clic sur l'IMAGE ne ferme pas (clic droit / zoom natif).
 *  L'object URL vient du MEME cache que la vignette (zero 2e fetch). */
function AttachmentLightbox({ item, onClose }: { item: AttachmentDto; onClose: () => void }) {
  const { t } = useTranslation();
  const url = useAttachmentObjectUrl(item.url);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
      role="dialog" aria-modal="true" aria-label={item.filename} onClick={onClose}>
      {url && (
        <img src={url} alt={item.filename}
          className="max-h-full max-w-full rounded-lg object-contain"
          onClick={(e) => e.stopPropagation()} />
      )}
      <button type="button" onClick={onClose} aria-label={t('feedback:thread.close')}
        className="touch-manipulation absolute right-3 top-3 rounded-md bg-black/40 p-1.5 text-white transition-colors hover:bg-black/60">
        <X size={16} aria-hidden />
      </button>
    </div>
  );
}

function ReactionPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative">
      <button type="button" onClick={() => setOpen((p) => !p)} aria-expanded={open}
        className="touch-manipulation rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
        <SmilePlus size={12} />
      </button>
      {open && (
        <span className="absolute bottom-full left-0 z-10 mb-1 flex gap-0.5 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          {REACTION_EMOJIS.map((emoji) => (
            <button key={emoji} type="button"
              onClick={() => { onPick(emoji); setOpen(false); }}
              className="touch-manipulation rounded px-1 py-0.5 text-field hover:bg-slate-100 dark:hover:bg-slate-700">
              {emoji}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

function TriagePanel({ ticketId, status, priority, adminNotes }: { ticketId: string; status: string; priority: string; adminNotes: string | null }) {
  const { t } = useTranslation();
  const triage = useTriage(ticketId);
  const [localStatus, setLocalStatus] = useState(status);
  const [localPriority, setLocalPriority] = useState(priority);
  const [localNotes, setLocalNotes] = useState(adminNotes ?? '');
  const [saved, setSaved] = useState(false);

  const dirty = localStatus !== status || localPriority !== priority || localNotes !== (adminNotes ?? '');
  const selectCls =
    'w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-field text-slate-700 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';

  return (
    <div className="rounded-xl bg-white px-4 py-3.5 shadow-sm dark:bg-slate-900">
      {/* Titre section interne = 14px semibold (UI_STANDARDS), labels = 12px. */}
      <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200">{t('feedback:triage.title')}</h3>
      <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:triage.status')}</span>
          <select className={selectCls} value={localStatus} onChange={(e) => setLocalStatus(e.target.value)}>
            {FEEDBACK_STATUSES.map((s) => <option key={s} value={s}>{t(`feedback:status.${s}`)}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:triage.priority')}</span>
          <select className={selectCls} value={localPriority} onChange={(e) => setLocalPriority(e.target.value)}>
            {FEEDBACK_PRIORITIES.map((p) => <option key={p} value={p}>{t(`feedback:priority.${p}`)}</option>)}
          </select>
        </label>
      </div>
      <label className="mt-2.5 block">
        <span className="mb-1 block text-meta font-medium text-slate-500 dark:text-slate-400">{t('feedback:triage.adminNotes')}</span>
        <textarea className={`${INPUT_BASE} min-h-14`} value={localNotes} maxLength={BODY_MAX}
          placeholder={t('feedback:triage.adminNotesPlaceholder')}
          onChange={(e) => setLocalNotes(e.target.value)} />
      </label>
      <div className="mt-2.5 flex items-center gap-2">
        <button type="button" className={`touch-manipulation ${BTN_PRIMARY} disabled:opacity-45`} disabled={!dirty || triage.isPending}
          onClick={() => {
            void triage.mutateAsync({
              status: localStatus !== status ? (localStatus as never) : undefined,
              priority: localPriority !== priority ? (localPriority as never) : undefined,
              adminNotes: localNotes !== (adminNotes ?? '') ? (localNotes || null) : undefined,
            }).then(() => { setSaved(true); setTimeout(() => setSaved(false), 2500); });
          }}>
          {t('feedback:triage.save')}
        </button>
        {saved && <span className="text-meta font-medium text-emerald-600 dark:text-emerald-400">{t('feedback:triage.saved')}</span>}
      </div>
    </div>
  );
}

function NotesPanel({ ticketId }: { ticketId: string }) {
  const { t, i18n } = useTranslation();
  const notes = useTicketNotes(ticketId, true);
  const addNote = useAddNote(ticketId);
  const [draft, setDraft] = useState('');

  return (
    <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-3.5 dark:border-amber-900/40 dark:bg-amber-950/20">
      <h3 className="text-body font-semibold text-amber-800 dark:text-amber-300">{t('feedback:thread.internalNotes')}</h3>
      <p className="mt-0.5 text-meta text-amber-700/80 dark:text-amber-400/80">{t('feedback:thread.internalNotesHint')}</p>
      {(notes.data ?? []).length === 0 ? (
        <p className="mt-2 text-meta italic text-amber-700/60 dark:text-amber-400/60">{t('feedback:thread.noteEmpty')}</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {(notes.data ?? []).map((n) => (
            <li key={n.id} className="rounded-lg bg-white/70 px-3 py-2 dark:bg-slate-900/50">
              <div className="text-meta text-amber-700/80 dark:text-amber-400/80">
                <span className="font-semibold">{n.authorName}</span> · {formatDateTime(n.createdAt, i18n.language)}
              </div>
              {/* Note = contenu de lecture secondaire : 13px (cran sm Option B). */}
              <p className="mt-1 whitespace-pre-wrap text-field leading-relaxed text-slate-700 dark:text-slate-200">{n.body}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-end gap-2">
        <textarea className={`${INPUT_BASE} min-h-10 flex-1`} value={draft} maxLength={BODY_MAX}
          placeholder={t('feedback:thread.notePlaceholder')}
          onChange={(e) => setDraft(e.target.value)} />
        <button type="button" className={`touch-manipulation ${BTN_PRIMARY} disabled:opacity-45`} disabled={addNote.isPending || !draft.trim()}
          onClick={() => void addNote.mutateAsync(draft.trim()).then(() => setDraft(''))}>
          {t('feedback:thread.noteSend')}
        </button>
      </div>
    </div>
  );
}

function HistoryPanel({ ticketId, active }: { ticketId: string; active: boolean }) {
  const { t, i18n } = useTranslation();
  const activity = useTicketActivity(ticketId, active);
  return (
    <div className="rounded-xl bg-white px-4 py-3.5 shadow-sm dark:bg-slate-900">
      <ul className="space-y-2">
        {(activity.data ?? []).map((a) => (
          <li key={a.id} className="flex items-baseline gap-2 text-meta">
            <span className="shrink-0 font-mono text-meta text-slate-400">{formatDateTime(a.at, i18n.language)}</span>
            <span className="text-slate-600 dark:text-slate-300">
              <span className="font-semibold">{a.byName}</span> {t(`feedback:activity.${a.action}`)}
              {a.detail ? <span className="text-slate-400"> — {a.detail}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
